import React, { useEffect, useCallback } from 'react';
import { User, UserFormData, CreditLimits, AccessType } from '../../types';
import { usersApi } from '../../services/api';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { showToast } from '../../store/slices/toastSlice';
import debounce from 'lodash/debounce';
import CreditLimitsForm from '../organizations/CreditLimitsForm';
import TrialManagement from './TrialManagement';
import { generateRandomPassword, passwordRules, validatePassword } from '../../utils/passwordGenerator';
import { KeyIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon, ClipboardIcon, CubeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { appFeaturesApi, FEATURE_DEFINITIONS } from '../../services/appFeaturesApi';

interface Organization {
  org_id: number;
  name: string;
  domain: string;
  status: string;
  billing_email: string;
  technical_contact_email: string;
  max_users: number;
  created_at: string;
  updated_at: string;
  settings: Record<string, any>;
}

interface UserFormProps {
  user?: User;
  onSubmit: (data: UserFormData) => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [formData, setFormData] = React.useState<UserFormData>({
    email: user?.email || '',
    password: '',
    role: user?.role || 'user',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    status: user?.status,
    organization_id: user?.organization_id || (currentUser?.role === 'org_admin' ? currentUser.organization_id : undefined),
    organization_role: user?.organizations?.[0]?.role || 'member',
    credit_limit: user?.credit_limit || 100,
    access_type: user?.access_type || ['web'],
    sign_up_origin: user?.sign_up_origin || (currentUser?.role === 'super_admin' ? 'super_admin' : 'org_admin')
  });

  const [creditLimits, setCreditLimits] = React.useState<CreditLimits>({
    credit_limit: user?.credit_limit || 100,
    access_type: user?.access_type || ['web', 'ide']
  });

  // Trial management state (only for super admins)
  const [trialInfo, setTrialInfo] = React.useState({
    web_trial: user?.web_trial || false,
    ide_trial: user?.ide_trial || false,
    security_trial: user?.security_trial || false,
    devops_trial: user?.devops_trial || false,
    web_trial_expires: user?.web_trial_expires || null,
    ide_trial_expires: user?.ide_trial_expires || null,
    security_trial_expires: user?.security_trial_expires || null,
    devops_trial_expires: user?.devops_trial_expires || null,
  });

  // App Features state
  const [availableFeatures, setAvailableFeatures] = React.useState<string[]>([]);
  const [userAppFeatures, setUserAppFeatures] = React.useState<string[]>([]);
  const [loadingFeatures, setLoadingFeatures] = React.useState(false);

  useEffect(() => {
    if (user) {
      loadUserLimits();
      // Update trial info when user data changes
      setTrialInfo({
        web_trial: user.web_trial || false,
        ide_trial: user.ide_trial || false,
        security_trial: user.security_trial || false,
        devops_trial: user.devops_trial || false,
        web_trial_expires: user.web_trial_expires || null,
        ide_trial_expires: user.ide_trial_expires || null,
        security_trial_expires: user.security_trial_expires || null,
        devops_trial_expires: user.devops_trial_expires || null,
      });

      // Load app features if super admin
      if (currentUser?.role === 'super_admin') {
        loadAppFeatures();
      }
    }
  }, [user, currentUser?.role]);

  const loadAppFeatures = async () => {
    if (!user || currentUser?.role !== 'super_admin') return;
    
    setLoadingFeatures(true);
    try {
      const [availableResponse, userFeaturesResponse] = await Promise.all([
        appFeaturesApi.getAvailableFeatures(),
        appFeaturesApi.getUserFeatures(user.user_id)
      ]);
      setAvailableFeatures(availableResponse.available_features);
      setUserAppFeatures(userFeaturesResponse.app_features);
    } catch (error) {
      console.error('Error loading app features:', error);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const loadUserLimits = async () => {
    try {
      if (user) {
        const response = await usersApi.getUserCreditLimits(user.user_id);
        const data = response.data;
        setCreditLimits({
          credit_limit: data.credit_limit,
          access_type: typeof data.access_type === 'string' ? data.access_type.split(',') : data.access_type
        });
      }
    } catch (error) {
      console.error('Failed to load user credit limits:', error);
    }
  };

  const [availableOrgs, setAvailableOrgs] = React.useState<Organization[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [orgUserCount, setOrgUserCount] = React.useState<{[key: number]: number}>({});

  // Load user's current organization
  const [currentOrg, setCurrentOrg] = React.useState<Organization | null>(null);
  const [currentOrgUserCount, setCurrentOrgUserCount] = React.useState<number>(0);
  const [currentUserOrg, setCurrentUserOrg] = React.useState<Organization | null>(null);

  // Password reset state
  const [resetPassword, setResetPassword] = React.useState<string>('');
  const [showResetPassword, setShowResetPassword] = React.useState<boolean>(false);
  const [isResettingPassword, setIsResettingPassword] = React.useState<boolean>(false);
  
  // Password visibility state for new user creation
  const [showCreatePassword, setShowCreatePassword] = React.useState<boolean>(false);

  useEffect(() => {
    const loadCurrentOrg = async () => {
      if (user?.organization_id) {
        try {
          const response = await usersApi.getUserOrganizations(user.user_id);
          if (response.data.success && response.data.data.length > 0) {
            setCurrentOrg(response.data.data[0]);
          }
        } catch (error) {
          console.error('Failed to load user organization:', error);
        }
      }
    };
    loadCurrentOrg();
  }, [user]);

  // Load user counts for organizations
  const loadOrgUserCounts = useCallback(async (orgs: Organization[]) => {
    const counts: {[key: number]: number} = {};
    
    for (const org of orgs) {
      try {
        const response = await usersApi.getUsers({ org_id: org.org_id, limit: 1 });
        if (response.data && response.data.meta) {
          counts[org.org_id] = response.data.meta.total;
        }
      } catch (error) {
        console.error(`Failed to load user count for org ${org.org_id}:`, error);
        counts[org.org_id] = 0;
      }
    }
    
    setOrgUserCount(counts);
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (search: string) => {
      if (currentUser?.role !== 'super_admin') return;
      
      try {
        setLoading(true);
        const response = await usersApi.getAllOrganizations({ search, limit: 100 });
        if (response.data.success && Array.isArray(response.data.data)) {
          setAvailableOrgs(response.data.data);
          // Load user counts for the organizations
          await loadOrgUserCounts(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load organizations:', error);
        setAvailableOrgs([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [loadOrgUserCounts]
  );

  // Load current org user count and organization data for org admins
  useEffect(() => {
    const loadCurrentOrgData = async () => {
      if (currentUser?.role === 'org_admin' && currentUser.organization_id) {
        try {
          // Load user count
          const userResponse = await usersApi.getUsers({ org_id: currentUser.organization_id, limit: 1 });
          if (userResponse.data && userResponse.data.meta) {
            setCurrentOrgUserCount(userResponse.data.meta.total);
          }
          
          // Load organization data
          const orgResponse = await usersApi.getAllOrganizations({ limit: 100 });
          if (orgResponse.data.success && Array.isArray(orgResponse.data.data)) {
            const userOrg = orgResponse.data.data.find((org: Organization) => org.org_id === currentUser.organization_id);
            if (userOrg) {
              setCurrentUserOrg(userOrg);
            }
          }
        } catch (error) {
          console.error('Failed to load current org data:', error);
        }
      }
    };
    
    loadCurrentOrgData();
  }, [currentUser]);

  // Initial load of organizations for super admin
  useEffect(() => {
    if (currentUser?.role === 'super_admin') {
      debouncedSearch(searchTerm);
    }
  }, [currentUser?.role]); // Only run on mount for super admin

  // Handle search and role changes
  useEffect(() => {
    if (currentUser?.role === 'super_admin') {
      debouncedSearch(searchTerm);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Clean and format the data before submission
      // Convert access_type to array for form data
      const accessTypes = Array.isArray(creditLimits.access_type) 
        ? creditLimits.access_type 
        : (creditLimits.access_type || '').split(',').filter((type): type is AccessType => 
            type === 'web' || type === 'ide' || type === 'security' || type === 'devops'
          );

      let cleanedData: UserFormData = {
        email: formData.email.trim(),
        password: formData.password || undefined,
        role: formData.role,
        first_name: formData.first_name?.trim(),
        last_name: formData.last_name?.trim(),
        status: formData.status,
        organization_id: formData.organization_id,
        organization_role: formData.organization_role,
        credit_limit: creditLimits.credit_limit,
        access_type: accessTypes,
        sign_up_origin: formData.sign_up_origin
      };

      // Handle organization assignment
      if (currentUser?.role === 'super_admin') {
        if (formData.organization_id) {
          cleanedData.organization_id = formData.organization_id;
          cleanedData.organization_role = formData.role === 'org_admin' ? 'admin' : 'member';
        }
      } else if (currentUser?.role === 'org_admin') {
        // Org admin can only add users to their org
        cleanedData.organization_id = currentUser.organization_id;
        cleanedData.organization_role = formData.role === 'org_admin' ? 'admin' : 'member';
        // Force sign_up_origin to be org_admin for org_admin users
        cleanedData.sign_up_origin = 'org_admin';
      }

      // For existing users, only send changed data
      if (user) {
        const updateData: any = {};
        
        // Check basic form fields for changes
        if (formData.email.trim() !== user.email) {
          updateData.email = formData.email.trim();
        }
        if (formData.role !== user.role) {
          updateData.role = formData.role;
        }
        if (formData.first_name?.trim() !== user.first_name) {
          updateData.first_name = formData.first_name?.trim();
        }
        if (formData.last_name?.trim() !== user.last_name) {
          updateData.last_name = formData.last_name?.trim();
        }
        if (formData.status !== user.status) {
          updateData.status = formData.status;
        }
        if (formData.organization_id !== user.organization_id) {
          updateData.organization_id = formData.organization_id;
          updateData.organization_role = formData.role === 'org_admin' ? 'admin' : 'member';
        }

        // Check credit limits for changes
        const originalAccessTypes = user.access_type || [];
        
        if (creditLimits.credit_limit !== user.credit_limit) {
          updateData.credit_limit = creditLimits.credit_limit;
        }
        
        // Compare access types arrays
        const accessTypesChanged = accessTypes.length !== originalAccessTypes.length || 
          !accessTypes.every(type => originalAccessTypes.includes(type));
        
        if (accessTypesChanged) {
          updateData.access_type = accessTypes;
        }

        // Include app features if super admin and they've changed
        if (currentUser?.role === 'super_admin') {
          const originalAppFeatures = user.app_features || [];
          const appFeaturesChanged = userAppFeatures.length !== originalAppFeatures.length || 
            !userAppFeatures.every(feature => originalAppFeatures.includes(feature));
          
          if (appFeaturesChanged) {
            updateData.app_features = userAppFeatures;
          }

          // Include trial information if changed
          const trialFields = [
            'web_trial', 'ide_trial', 'security_trial', 'devops_trial',
            'web_trial_expires', 'ide_trial_expires', 'security_trial_expires', 'devops_trial_expires'
          ];
          
          trialFields.forEach(field => {
            const currentValue = trialInfo[field as keyof typeof trialInfo];
            const originalValue = user[field as keyof User];
            
            if (currentValue !== originalValue) {
              updateData[field] = currentValue || null;
            }
          });
        }

        // Only make API call if there are changes
        if (Object.keys(updateData).length > 0) {
          await usersApi.updateUserComplete(user.user_id, updateData);
        }
      } else {
        // For new users, use the original onSubmit
        await onSubmit(cleanedData);
      }

      dispatch(showToast({
        message: user 
          ? 'User updated successfully' 
          : 'User created successfully',
        type: 'success'
      }));
    } catch (error) {
      dispatch(showToast({
        message: 'Failed to save user',
        type: 'error'
      }));
      console.error('Failed to save user:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'organization_id' 
        ? (value ? parseInt(value, 10) : undefined)
        : name === 'email' 
        ? value.toLowerCase()
        : value,
    }));

    // Clear organization_id when role changes from org_admin
    if (name === 'role' && value !== 'org_admin') {
      setFormData(prev => ({ ...prev, organization_id: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-900 dark:text-white">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-2 block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
          />
        </div>

        {!user && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900 dark:text-white">
              Password
            </label>
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <input
                  type={showCreatePassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!user}
                  className="flex-1 px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                />
                <button
                  type="button"
                  onClick={() => setShowCreatePassword(!showCreatePassword)}
                  className="px-3 py-2 rounded-xl bg-gray-600 hover:bg-gray-700 text-white transition-colors duration-200 flex items-center"
                  title={showCreatePassword ? 'Hide password' : 'Show password'}
                >
                  {showCreatePassword ? (
                    <EyeSlashIcon className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
                {formData.password && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(formData.password || '');
                        dispatch(showToast({
                          message: 'Password copied to clipboard',
                          type: 'success'
                        }));
                      } catch (error) {
                        dispatch(showToast({
                          message: 'Failed to copy password',
                          type: 'error'
                        }));
                      }
                    }}
                    className="px-3 py-2 rounded-xl bg-gray-600 hover:bg-gray-700 text-white transition-colors duration-200 flex items-center"
                    title="Copy password to clipboard"
                  >
                    <ClipboardIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const newPassword = generateRandomPassword();
                    setFormData(prev => ({ ...prev, password: newPassword }));
                    dispatch(showToast({
                      message: 'Random password generated',
                      type: 'success'
                    }));
                  }}
                  className="px-3 py-2 rounded-xl bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200 flex items-center gap-1"
                  title="Generate random password"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Generate
                </button>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {passwordRules.description}
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-900 dark:text-white">
            First Name
          </label>
          <input
            type="text"
            name="first_name"
            id="first_name"
            value={formData.first_name}
            onChange={handleChange}
            className="mt-2 block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
          />
        </div>

        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-900 dark:text-white">
            Last Name
          </label>
          <input
            type="text"
            name="last_name"
            id="last_name"
            value={formData.last_name}
            onChange={handleChange}
            className="mt-2 block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
          />
        </div>

        {(currentUser?.role === 'super_admin' || currentUser?.role === 'org_admin') && (
          <>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-900 dark:text-white">
                Role
              </label>
              <select
                name="role"
                id="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="mt-2 block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 cursor-pointer"
              >
                <option value="user">User</option>
                <option value="org_admin">Organization Admin</option>
                {currentUser?.role === 'super_admin' && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>


            {currentUser?.role === 'super_admin' && (
              <div className="sm:col-span-2">
                <label htmlFor="organization_id" className="block text-sm font-medium text-gray-900 dark:text-white">
                  Organization
                </label>
                {currentOrg && (
                  <div className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                    Current: {currentOrg.name} ({currentOrg.domain})
                  </div>
                )}
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                  />
                  <select
                    name="organization_id"
                    id="organization_id"
                    value={formData.organization_id || ''}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 cursor-pointer"
                    size={5}
                  >
                    <option value="">Select an organization</option>
                    {availableOrgs.map((org) => {
                      const userCount = orgUserCount[org.org_id] || 0;
                      const isAtLimit = userCount >= org.max_users;
                      const limitText = `${userCount}/${org.max_users} users`;
                      
                      return (
                        <option 
                          key={org.org_id} 
                          value={org.org_id}
                          disabled={isAtLimit && !user} // Disable if at limit and creating new user
                        >
                          {org.name} ({org.domain}) - {limitText} - {org.status.toUpperCase()}
                          {isAtLimit ? ' (FULL)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {loading && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Loading organizations...</p>
                )}
              </div>
            )}
          </>
        )}

        {user && (
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-900 dark:text-white">
              Status
            </label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-2 block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 cursor-pointer"
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        )}
      </div>

      {(currentUser?.role === 'super_admin' || currentUser?.role === 'org_admin') && (
        <CreditLimitsForm
          limits={creditLimits}
          onChange={setCreditLimits}
          isOrgAdmin={currentUser?.role === 'org_admin'}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* Organization User Limit Display for Org Admins */}
      {currentUser?.role === 'org_admin' && !user && currentUserOrg && currentUserOrg.max_users > 0 && currentOrgUserCount >= currentUserOrg.max_users && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-900 dark:text-red-200 mb-2">
            Organization User Limit
          </h4>
          <div className="text-sm text-red-800 dark:text-red-300">
            ⚠️ Organization has reached maximum user limit ({currentOrgUserCount}/{currentUserOrg.max_users}). Cannot create new users.
          </div>
        </div>
      )}


      {/* Password Reset - For existing users */}
      {user && (currentUser?.role === 'super_admin' || 
        (currentUser?.role === 'org_admin' && user.organization_id === currentUser.organization_id)) && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-3 flex items-center gap-2">
              <KeyIcon className="w-4 h-4" />
              Password Reset
            </h4>
            <div className="space-y-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Generate a new random password for this user. The user will need to use this new password to log in.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        setIsResettingPassword(true);
                        const newPassword = generateRandomPassword();
                        await usersApi.resetUserPassword(user.user_id, newPassword);
                        
                        // Set the new password in state to display it
                        setResetPassword(newPassword);
                        setShowResetPassword(false); // Start hidden
                        
                        dispatch(showToast({
                          message: 'Password reset successful',
                          type: 'success'
                        }));
                      } catch (error) {
                        dispatch(showToast({
                          message: 'Failed to reset password',
                          type: 'error'
                        }));
                        console.error('Failed to reset password:', error);
                      } finally {
                        setIsResettingPassword(false);
                      }
                    }}
                    disabled={isResettingPassword}
                    className="px-4 py-2 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-400 disabled:cursor-not-allowed text-white transition-colors duration-200 flex items-center gap-2"
                  >
                    <ArrowPathIcon className={`w-4 h-4 ${isResettingPassword ? 'animate-spin' : ''}`} />
                    {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <div className="text-xs text-yellow-700 dark:text-yellow-300">
                    {passwordRules.description}
                  </div>
                </div>

                {/* New Password Display */}
                {resetPassword && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-yellow-900 dark:text-yellow-200">
                      New Password
                    </label>
                    <div className="flex gap-2">
                      <input
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPassword}
                        readOnly
                        className="flex-1 px-4 py-2 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="px-3 py-2 rounded-xl bg-yellow-600 hover:bg-yellow-700 text-white transition-colors duration-200 flex items-center"
                        title={showResetPassword ? 'Hide password' : 'Show password'}
                      >
                        {showResetPassword ? (
                          <EyeSlashIcon className="w-4 h-4" />
                        ) : (
                          <EyeIcon className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(resetPassword);
                            dispatch(showToast({
                              message: 'Password copied to clipboard',
                              type: 'success'
                            }));
                          } catch (error) {
                            dispatch(showToast({
                              message: 'Failed to copy password',
                              type: 'error'
                            }));
                          }
                        }}
                        className="px-3 py-2 rounded-xl bg-yellow-600 hover:bg-yellow-700 text-white transition-colors duration-200 flex items-center"
                        title="Copy password to clipboard"
                      >
                        <ClipboardIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      Share this password with the user. They will need to use this to log in.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Features Management - Super Admin Only */}
      {user && currentUser?.role === 'super_admin' && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                  <CubeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    User App Features
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Control individual feature flags for this user. These are in addition to organization features.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {loadingFeatures ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading features...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                          Individual User Features
                        </h4>
                        <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                          <li>• These features are specific to this user only</li>
                          <li>• User also inherits all features from their organization</li>
                          <li>• Individual features are additive to organization features</li>
                          <li>• Changes here only affect this specific user</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableFeatures.map((feature) => {
                      const featureInfo = FEATURE_DEFINITIONS[feature];
                      const isEnabled = userAppFeatures.includes(feature);
                      
                      return (
                        <div key={feature} className="flex items-start space-x-3 p-4 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                          <input
                            type="checkbox"
                            id={`user-feature-${feature}`}
                            checked={isEnabled}
                            onChange={(e) => {
                              const newFeatures = e.target.checked
                                ? [...userAppFeatures, feature]
                                : userAppFeatures.filter(f => f !== feature);
                              
                              setUserAppFeatures(newFeatures);
                            }}
                            disabled={loadingFeatures}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <label htmlFor={`user-feature-${feature}`} className="text-sm font-medium cursor-pointer text-gray-900 dark:text-white">
                              {featureInfo?.displayName || feature}
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {featureInfo?.description || 'No description available'}
                            </p>
                          </div>
                          {isEnabled && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckIcon className="w-3 h-3 mr-1" />
                              Enabled
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {userAppFeatures.length > 0 && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                      <h4 className="font-medium mb-2 text-green-900 dark:text-green-200">
                        Active Individual Features ({userAppFeatures.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {userAppFeatures.map((feature) => (
                          <span key={feature} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {FEATURE_DEFINITIONS[feature]?.displayName || feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trial Management - Only for Super Admins and existing users */}
      {currentUser?.role === 'super_admin' && user && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <TrialManagement
            trials={trialInfo}
            onChange={setTrialInfo}
            level="user"
          />
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-xl border border-light-border dark:border-dark-border text-gray-900 dark:text-white bg-light-card dark:bg-dark-card hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200"
        >
          {user ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
